import { useEffect, useState, useCallback } from "react";
import { Wallet, Snowflake, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import LoadingScreen from "@/components/LoadingScreen";
import { USER } from "@/constants/testIds";
import { formatDateTime, formatINR } from "@/lib/utils";

export default function CreditsPage() {
  const [balance, setBalance] = useState(null);
  const [plans, setPlans] = useState([]);
  const [txns, setTxns] = useState([]);
  const [customCredits, setCustomCredits] = useState(5);
  const [requesting, setRequesting] = useState(false);

  const load = useCallback(async () => {
    const [b, p, t] = await Promise.all([
      api.get("/credits/balance"),
      api.get("/plans"),
      api.get("/credits/transactions?limit=100"),
    ]);
    setBalance(b.data); setPlans(p.data); setTxns(t.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const buy = async (details = {}) => {
    const payload = {
      plan_id: details.planId,
      custom_credits: details.customCredits,
    };

    try {
      setRequesting(true);
      await api.post("/credits/request", payload);
      const message = details.planName
        ? `Your request for ${details.planName} has been received. Our team will review it and activate your credits shortly.`
        : `Your request for ${details.customCredits} credits has been received. Our team will review it and activate your credits shortly.`;
      toast.success(message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Request failed");
    } finally {
      setRequesting(false);
    }
  };

  if (!balance) return <LoadingScreen />;

  return (
    <div className="space-y-6">
      {/* Balance cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="sqit-card p-6 bg-[#EAF4E3] border-[#0B6936]/30">
          <Wallet size={20} className="text-[#0B6936]" />
          <div className="mt-3 text-xs text-gray-600 uppercase tracking-wider font-semibold">Available</div>
          <div className="font-display text-4xl font-bold text-[#0B6936]">{balance.available}</div>
          <div className="text-xs text-gray-600 mt-1">credits ready to use</div>
        </div>
        <div className="sqit-card p-6">
          <Snowflake size={20} className="text-gray-500" />
          <div className="mt-3 text-xs text-gray-600 uppercase tracking-wider font-semibold">Frozen</div>
          <div className="font-display text-4xl font-bold text-gray-900">{balance.frozen}</div>
          <div className="text-xs text-gray-600 mt-1">reserved for pending jobs</div>
        </div>
        <div className="sqit-card p-6">
          <CheckCircle2 size={20} className="text-[#0B6936]" />
          <div className="mt-3 text-xs text-gray-600 uppercase tracking-wider font-semibold">Lifetime consumed</div>
          <div className="font-display text-4xl font-bold text-gray-900">
            {txns.filter((t) => t.kind === "consume").reduce((s, t) => s + Math.abs(t.delta), 0)}
          </div>
          <div className="text-xs text-gray-600 mt-1">credits used on completed jobs</div>
        </div>
      </div>

      {/* Plans */}
      <div className="sqit-card p-6">
        <h3 className="font-display text-lg font-semibold text-gray-900">Credit packs</h3>
        <p className="text-sm text-gray-500 mt-1">Credits never expire. Refunded automatically for failed or rejected jobs.</p>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {plans.map((p) => (
            <div key={p.id} className={`sqit-card p-6 ${p.highlighted
              ? "border-[#0B6936] ring-2 ring-[#0B6936]/20"
              : ""}`}>
              {p.highlighted && (
                <div className="inline-block px-2 py-0.5 rounded-full bg-[#0B6936] text-white text-[10px] font-semibold tracking-wide mb-2">
                  MOST POPULAR
                </div>
              )}
              <div className="font-display text-xl font-semibold text-gray-900">{p.name}</div>
              <div className="text-xs text-gray-500 mt-1">{p.tagline}</div>
              <div className="mt-4 font-display text-3xl font-bold text-gray-900">{formatINR(p.price_inr)}</div>
              <div className="text-xs text-gray-500">{p.credits} credits</div>
              <button
                type="button"
                onClick={() => buy({ planId: p.id, planName: p.name })}
                disabled={requesting}
                data-testid={USER.buyPlan(p.id)}
                className="w-full mt-5 bg-[#0B6936] text-white hover:bg-[#09582D] rounded-md px-4 py-2.5 text-sm font-semibold disabled:opacity-60"
              >
                Buy {p.name}
              </button>
            </div>
          ))}
          <div className="sqit-card p-6">
            <div className="font-display text-xl font-semibold text-gray-900">
              Custom Credits
            </div>

            <div className="text-xs text-gray-500 mt-1">
              Choose your own quantity
            </div>

            <div className="mt-4 font-display text-3xl font-bold text-gray-900">
              ₹500
            </div>

            <div className="text-xs text-gray-500">
              per credit
            </div>

            <input
              data-testid={USER.buyCustomInput}
              type="number"
              min={1}
              value={customCredits}
              onChange={(e) => setCustomCredits(e.target.value)}
              placeholder="Credits"
              className="w-full mt-5 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B6936]/20 focus:border-[#0B6936]"
            />

            <button
              type="button"
              onClick={() =>
                buy({
                  customCredits: Number(customCredits),
                })
              }
              disabled={requesting || !customCredits}
              data-testid={USER.buyCustomSubmit}
              className="w-full mt-3 bg-[#0B6936] text-white hover:bg-[#09582D] rounded-md px-4 py-2.5 text-sm font-semibold disabled:opacity-60"
            >
              Request for Credits
            </button>
          </div>
        </div>
      </div>

      {/* Transactions */}
      <div className="sqit-card overflow-hidden">
        <div className="p-6 pb-4">
          <h3 className="font-display text-lg font-semibold text-gray-900">Transaction history</h3>
        </div>
        {txns.length === 0 ? (
          <div className="p-8 text-center text-gray-500 border-t border-[#E5E7EB]">No transactions yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-y border-[#E5E7EB]">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Delta</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Balance</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Reference</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">When</th>
              </tr>
            </thead>
            <tbody>
              {txns.map((t) => (
                <tr key={t.id} className="border-b border-gray-100">
                  <td className="px-6 py-3 capitalize">{t.kind}</td>
                  <td className={`px-6 py-3 font-semibold ${t.delta >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>
                    {t.delta > 0 ? "+" : ""}{t.delta}
                  </td>
                  <td className="px-6 py-3 text-gray-700">{t.balance_after}</td>
                  <td className="px-6 py-3 text-xs text-gray-500">
                    {t.meta?.job_code || t.meta?.invoice_no || t.meta?.label || "—"}
                  </td>
                  <td className="px-6 py-3 text-xs text-gray-500">{formatDateTime(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}
